import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from './firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { el } from './i18n/el';
import {
  buildVatRowsFromSources,
  normalizeGreekVat,
  updateInvoicesForVat,
} from './utils/vatMapping';

function statusBadge(row) {
  if (!row.hasMapping) {
    return { label: el.vatMapFromScans, className: 'bg-amber-50 text-amber-800 border-amber-200' };
  }
  if (row.needsReview) {
    return { label: el.vatMapNeedsReview, className: 'bg-orange-50 text-orange-800 border-orange-200' };
  }
  return { label: el.vatMapMapped, className: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
}

export default function VatMappings() {
  const [mappings, setMappings] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ vat: '', companyName: '' });
  const [editId, setEditId] = useState(null);

  const navigate = useNavigate();
  const user = auth.currentUser;

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: '' }), 4000);
  };

  const loadMappings = async (cId) => {
    setLoading(true);
    try {
      const [mappingSnap, invoiceSnap] = await Promise.all([
        getDocs(collection(db, 'companies', cId, 'vatMappings')),
        getDocs(collection(db, 'companies', cId, 'invoices')),
      ]);

      const mappingRows = mappingSnap.docs
        .map((d) => ({
          id: d.id,
          vat: normalizeGreekVat(d.data().vat || d.id),
          companyName: (d.data().companyName || '').trim(),
          updatedAt: d.data().updatedAt || '',
        }))
        .filter((r) => r.vat && r.companyName);

      const invoices = invoiceSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMappings(buildVatRowsFromSources(mappingRows, invoices));
    } catch {
      showToast(el.vatMapLoadFailed, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', user.uid));
        if (companyDoc.exists()) {
          setUserRole('owner');
          setCompanyId(user.uid);
          await loadMappings(user.uid);
          return;
        }

        const membershipDoc = await getDoc(doc(db, 'userMemberships', user.uid));
        if (!membershipDoc.exists()) {
          showToast(el.vatMapNoAccess, 'error');
          navigate('/');
          return;
        }

        const data = membershipDoc.data();
        const role = data.role || 'accountant';
        setCompanyId(data.companyId);
        setUserRole(role);

        if (role !== 'owner' && role !== 'manager') {
          navigate('/');
          return;
        }

        await loadMappings(data.companyId);
      } catch {
        showToast(el.vatMapLoadFailed, 'error');
      }
    };

    init();
  }, [user, navigate]);

  const filtered = useMemo(() => {
    let rows = mappings;
    if (filterMode === 'review') rows = rows.filter((m) => m.needsReview);
    if (filterMode === 'mapped') rows = rows.filter((m) => m.hasMapping && !m.needsReview);
    if (filterMode === 'scanned') rows = rows.filter((m) => !m.hasMapping);

    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (m) =>
        m.vat.includes(q) ||
        m.companyName.toLowerCase().includes(q) ||
        m.alternateNames?.some((n) => n.toLowerCase().includes(q)),
    );
  }, [mappings, search, filterMode]);

  const resetForm = () => {
    setForm({ vat: '', companyName: '' });
    setEditId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const vat = normalizeGreekVat(form.vat);
    const companyName = form.companyName.trim();

    if (!vat) {
      showToast(el.vatMapInvalidVat, 'error');
      return;
    }
    if (!companyName) {
      showToast(el.vatMapNameRequired, 'error');
      return;
    }

    const duplicate = mappings.find((m) => m.vat === vat && m.id !== editId && m.hasMapping);
    if (duplicate && editId !== vat) {
      showToast(el.vatMapDuplicate, 'error');
      return;
    }

    setSaving(true);
    try {
      if (editId && editId !== vat) {
        await deleteDoc(doc(db, 'companies', companyId, 'vatMappings', editId));
      }

      await setDoc(doc(db, 'companies', companyId, 'vatMappings', vat), {
        vat,
        companyName,
        updatedAt: new Date().toISOString(),
        updatedBy: { uid: user.uid, email: user.email || '' },
      });

      const synced = await updateInvoicesForVat(companyId, vat, companyName);

      if (synced > 0) {
        showToast(`${editId ? el.vatMapUpdated : el.vatMapAdded}. ${el.vatMapSynced(synced)}`, 'success');
      } else {
        showToast(editId ? el.vatMapUpdated : el.vatMapAdded, 'success');
      }

      resetForm();
      await loadMappings(companyId);
    } catch {
      showToast(el.vatMapSaveFailed, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditId(row.hasMapping ? row.id : row.vat);
    setForm({ vat: row.vat, companyName: row.companyName });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    if (!confirmDelete || !confirmDelete.hasMapping) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'vatMappings', confirmDelete.id));
      showToast(el.vatMapDeleted, 'success');
      if (editId === confirmDelete.id) resetForm();
      await loadMappings(companyId);
    } catch {
      showToast(el.vatMapDeleteFailed, 'error');
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  if (loading && mappings.length === 0 && userRole !== 'accountant') {
    return (
      <div className="max-w-5xl mx-auto py-10 flex justify-center text-gray-600">
        {el.vatMapLoading}
      </div>
    );
  }

  if (userRole !== 'owner' && userRole !== 'manager') {
    return (
      <div className="max-w-5xl mx-auto py-10">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{el.vatMapAccessDenied}</h2>
          <p className="text-gray-600 mb-4">{el.vatMapAccessDeniedBody}</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            {el.vatMapBack}
          </button>
        </div>
      </div>
    );
  }

  const reviewCount = mappings.filter((m) => m.needsReview).length;

  return (
    <div className="max-w-5xl mx-auto py-6">
      {toast.visible && (
        <div className={`fixed top-4 right-4 px-4 py-3 rounded shadow-lg z-50 text-white max-w-sm ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{el.vatMapConfirmDeleteTitle}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {el.vatMapConfirmDeleteBody(confirmDelete.vat, confirmDelete.companyName)}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">
                {el.vatMapCancel}
              </button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700">
                {el.vatMapDelete}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-blue-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">{el.vatMapTitle}</h2>
          <p className="text-blue-100 text-sm">{el.vatMapSubtitle}</p>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900">
            {el.vatMapHint}
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              {editId ? el.vatMapEditTitle : el.vatMapAddTitle}
            </h3>
            <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{el.fieldIssuerVat}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                  value={form.vat}
                  onChange={(e) => setForm({ ...form, vat: e.target.value })}
                  placeholder="123456789"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{el.fieldIssuerName}</label>
                <input
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                  value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                  placeholder={el.vatMapNamePlaceholder}
                  required
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                {editId && (
                  <button type="button" onClick={resetForm} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">
                    {el.vatMapCancel}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? el.vatMapSaving : (editId ? el.vatMapSaveAndSync : el.vatMapAdd)}
                </button>
              </div>
            </form>
          </div>

          <div>
            <div className="flex flex-col gap-3 mb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-800">
                  {el.vatMapListTitle} ({filtered.length})
                  {reviewCount > 0 && (
                    <span className="ml-2 text-sm font-normal text-orange-600">
                      · {reviewCount} {el.vatMapNeedsReview.toLowerCase()}
                    </span>
                  )}
                </h3>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={el.vatMapSearchPlaceholder}
                  className="w-full sm:w-64 border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  ['all', 'Όλα'],
                  ['review', el.vatMapNeedsReview],
                  ['scanned', el.vatMapFromScans],
                  ['mapped', el.vatMapMapped],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilterMode(key)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      filterMode === key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="bg-gray-50 p-6 text-center rounded-lg border border-gray-200 text-gray-500">
                {search || filterMode !== 'all' ? el.vatMapNoResults : el.vatMapEmpty}
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{el.fieldIssuerVat}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{el.fieldIssuerName}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Κατάσταση</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filtered.map((row) => {
                      const badge = statusBadge(row);
                      return (
                        <tr key={row.vat} className={row.needsReview ? 'bg-orange-50/40' : undefined}>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono text-gray-900">{row.vat}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{el.vatMapInvoiceCount(row.invoiceCount)}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">{row.companyName || '—'}</div>
                            {row.alternateNames?.length > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                {el.vatMapAlternateNames}: {row.alternateNames.join(' · ')}
                              </div>
                            )}
                            {row.sampleInvoiceUrl && (
                              <a
                                href={row.sampleInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 font-medium mt-1.5"
                                title={el.dashOpenInNewWindow}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                {el.dashPreviewLink}
                              </a>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                            <button onClick={() => handleEdit(row)} className="text-blue-600 hover:text-blue-800 mr-3">
                              {row.hasMapping ? el.vatMapEdit : el.vatMapRegister}
                            </button>
                            {row.hasMapping && (
                              <button onClick={() => setConfirmDelete(row)} className="text-red-600 hover:text-red-800">
                                {el.vatMapDelete}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
